import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileDown, Presentation } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Spinner } from 'components/ui/spinner'
import { toast } from 'components/ui/toast-store'

import { presentationPresentPath } from '../featureFlag'
import * as presentationService from '../services/presentationService'
import type { DeckDefinition, ElementDescriptor, SnapshotPreflight, SnapshotSummary } from '../types/presentation.types'
import { rasterizeDeck, type RasterProgress } from '../utils/rasterize'

/**
 * Publish a snapshot of the deck (MYD-508).
 *
 * A snapshot freezes the SAVED deck as it is now: its data, and pictures of
 * its maps and charts. It is what a share link, a PDF and PowerPoint are made
 * from, so the person you send it to sees what you saw, even after the data
 * moves on. Snapshots are numbered and never change; publishing again makes
 * the next one.
 *
 * Before publishing, the dialog says what is about to be frozen that the
 * owner may not have thought about: financial figures, which anyone given the
 * snapshot will see, and satellite maps, which are captured as street maps
 * because satellite imagery may not be stored.
 */
interface PublishDialogProps {
	open: boolean
	presentationId: string
	/** The saved deck. The caller saves before opening this. */
	deck: DeckDefinition
	elements: Record<string, ElementDescriptor>
	onClose: () => void
}

type Phase = 'idle' | 'resolving' | 'capturing' | 'saving'

const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`

const PHASE_LABEL: Record<Exclude<Phase, 'idle'>, string> = {
	resolving: 'Loading the data...',
	capturing: 'Capturing maps and charts...',
	saving: 'Saving the snapshot...',
}

export function PublishDialog({ open, presentationId, deck, elements, onClose }: PublishDialogProps) {
	const [preflight, setPreflight] = useState<SnapshotPreflight | null>(null)
	const [snapshots, setSnapshots] = useState<SnapshotSummary[]>([])
	const [loadError, setLoadError] = useState<string | null>(null)
	const [phase, setPhase] = useState<Phase>('idle')
	const [progress, setProgress] = useState<RasterProgress>({ done: 0, total: 0 })
	const [error, setError] = useState<string | null>(null)
	const [published, setPublished] = useState<{ version: number; failed: number } | null>(null)
	// PDF export (MYD-510): which version is being made, and, for a deck about
	// several assets, the choice of where to file it.
	const [exporting, setExporting] = useState<{ version: number; format: 'pdf' | 'pptx' } | null>(null)
	const [choosing, setChoosing] = useState<{ version: number; options: presentationService.FilingOption[] } | null>(null)

	useEffect(() => {
		if (!open) return
		let cancelled = false
		setPreflight(null)
		setLoadError(null)
		setError(null)
		setPublished(null)
		presentationService
			.listSnapshots(presentationId)
			.then(result => {
				if (cancelled) return
				setPreflight(result.preflight)
				setSnapshots(result.snapshots)
			})
			.catch(() => !cancelled && setLoadError('The snapshots could not be loaded.'))
		return () => {
			cancelled = true
		}
	}, [open, presentationId])

	const busy = phase !== 'idle'

	const publish = async () => {
		setError(null)
		setPublished(null)
		try {
			setPhase('resolving')
			// Resolved here rather than taken from the editor, which only
			// resolves the slides that have been looked at.
			const { blocks } = await presentationService.resolvePresentation(presentationId)
			setPhase('capturing')
			const { rasters, failed } = await rasterizeDeck({ deck, blocks, elements, theme: deck.theme, onProgress: setProgress })
			setPhase('saving')
			const snapshot = await presentationService.createSnapshot(presentationId, rasters)
			setSnapshots(current => [snapshot, ...current])
			setPublished({ version: snapshot.version, failed: failed.length })
		} catch (cause: any) {
			setError(cause?.response?.data?.message ?? 'The snapshot could not be published.')
		} finally {
			setPhase('idle')
		}
	}

	// PowerPoint (MYD-512): downloaded only, never filed, so never a choice to make.
	const exportPptx = async (version: number) => {
		setExporting({ version, format: 'pptx' })
		setChoosing(null)
		try {
			const result = await presentationService.exportSnapshotPptx(presentationId, version)
			if (result.kind === 'pdf') downloadBlob(result.blob, result.filename)
		} catch (cause: any) {
			toast.error(cause?.response?.data?.message ?? cause?.message ?? 'The PowerPoint file could not be made.')
		} finally {
			setExporting(null)
		}
	}

	const exportPdf = async (version: number, fileTo?: Parameters<typeof presentationService.exportSnapshotPdf>[2]) => {
		setExporting({ version, format: 'pdf' })
		setChoosing(null)
		try {
			const result = await presentationService.exportSnapshotPdf(presentationId, version, fileTo)
			if (result.kind === 'choose') {
				setChoosing({ version, options: result.options })
				return
			}
			downloadBlob(result.blob, result.filename)
			if (result.filed === 'failed') toast.error('The PDF downloaded, but it could not be filed in Documents.')
			else if (result.filed !== 'none') toast.success('The PDF downloaded, and a copy is in the asset’s Documents tab.')
		} catch (cause: any) {
			toast.error(cause?.response?.data?.message ?? cause?.message ?? 'The PDF could not be made.')
		} finally {
			setExporting(null)
		}
	}

	const close = () => !busy && onClose()

	return (
		<Dialog open={open} onClose={close}>
			<DialogContent className="sm:max-w-[520px]">
				<DialogHeader>
					<DialogTitle>Publish a snapshot</DialogTitle>
					<DialogDescription>
						A snapshot freezes this deck as it is now, with pictures of its maps and charts. Share links, PDFs and PowerPoint files are made from it, so they do not change when
						the data does.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 px-5 py-4">
					{loadError ? (
						<p className="text-sm text-destructive">{loadError}</p>
					) : !preflight ? (
						<div className="flex justify-center py-4">
							<Spinner />
						</div>
					) : (
						<>
							<p className="text-sm text-gray-700">
								{plural(preflight.slideCount, 'slide')}, {plural(preflight.rasterBlocks, 'map or chart', 'maps and charts')} to capture.
							</p>
							{(preflight.financialBlocks > 0 || preflight.satelliteMaps > 0) && (
								<ul className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-gray-800" data-testid="publish-warnings">
									{preflight.financialBlocks > 0 && (
										<li>
											{plural(preflight.financialBlocks, 'element shows', 'elements show')} financial figures. Anyone you share this snapshot with will see them.
										</li>
									)}
									{preflight.satelliteMaps > 0 && (
										<li>
											{plural(preflight.satelliteMaps, 'satellite map')} will be captured as {preflight.satelliteMaps === 1 ? 'a street map' : 'street maps'}: satellite imagery
											cannot be stored.
										</li>
									)}
								</ul>
							)}
						</>
					)}

					{busy && (
						<div className="flex items-center gap-2 text-sm text-gray-600" aria-live="polite">
							<Spinner type="circular" width="16" containerHeight="16px" className="w-auto" />
							<span>
								{PHASE_LABEL[phase as Exclude<Phase, 'idle'>]}
								{phase === 'capturing' && progress.total > 0 && ` ${progress.done} of ${progress.total}`}
							</span>
						</div>
					)}
					{published && (
						<p className="text-sm text-success" aria-live="polite">
							Published version {published.version}.
							{published.failed > 0 && ` ${plural(published.failed, 'map or chart', 'maps and charts')} could not be captured and will show as unavailable.`}
						</p>
					)}
					{error && <p className="text-sm text-destructive">{error}</p>}

					{snapshots.length > 0 && (
						<div>
							<h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Published</h3>
							<ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200" aria-label="Published snapshots">
								{snapshots.map(snapshot => (
									<li key={snapshot.version} className="flex items-center gap-3 px-3 py-2 text-sm">
										<span className="font-medium text-gray-900">Version {snapshot.version}</span>
										<span className="text-gray-500">{new Date(snapshot.createdAt).toLocaleString()}</span>
										<Link
											to={`${presentationPresentPath(presentationId)}?snapshot=${snapshot.version}`}
											target="_blank"
											rel="noopener"
											className="ml-auto text-primary hover:underline"
										>
											View
										</Link>
										<button
											type="button"
											onClick={() => exportPdf(snapshot.version)}
											disabled={exporting !== null}
											className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
											aria-label={`Download version ${snapshot.version} as PDF`}
										>
											<FileDown className="h-3.5 w-3.5" />
											{exporting?.version === snapshot.version && exporting.format === 'pdf' ? 'Making PDF...' : 'PDF'}
										</button>
										<button
											type="button"
											onClick={() => exportPptx(snapshot.version)}
											disabled={exporting !== null}
											className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
											aria-label={`Download version ${snapshot.version} as PowerPoint`}
										>
											<Presentation className="h-3.5 w-3.5" />
											{exporting?.version === snapshot.version && exporting.format === 'pptx' ? 'Making file...' : 'PowerPoint'}
										</button>
									</li>
								))}
							</ul>
						</div>
					)}

					{choosing && (
						<div className="rounded-md border border-gray-200 p-3 text-sm" role="group" aria-label="Where to file the PDF">
							<p className="mb-2 text-gray-700">This deck is about more than one asset. Which Documents tab should the PDF go in?</p>
							<div className="flex flex-wrap gap-2">
								{choosing.options.map(option => (
									<Button key={`${option.scope}:${option.entityId}`} variant="ghost" isOutline onClick={() => exportPdf(choosing.version, { scope: option.scope, entityId: option.entityId })}>
										{option.name}
									</Button>
								))}
								<Button variant="ghost" isOutline onClick={() => exportPdf(choosing.version, 'none')}>
									Don’t file it, just download
								</Button>
							</div>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="ghost" isOutline onClick={close} disabled={busy}>
						Close
					</Button>
					<Button onClick={publish} disabled={busy || !preflight || preflight.slideCount === 0}>
						{busy ? 'Publishing...' : snapshots.length ? 'Publish new version' : 'Publish'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default PublishDialog

/** Hand a blob to the browser as a download. */
const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob)
	const anchor = document.createElement('a')
	anchor.href = url
	anchor.download = filename
	document.body.appendChild(anchor)
	anchor.click()
	anchor.remove()
	setTimeout(() => URL.revokeObjectURL(url), 1000)
}
